import { Injectable } from '@angular/core';
import { CXoneAcdClient } from '@nice-devone/acd-sdk';
import { Subject, Subscription } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { GenericModalService } from 'src/app/shared/services/generic-modal.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';
import { EventHandlerService } from '../../services/event-handler.service';
import {
  AgentSessionEndEvent,
  AgentSessionStatus,
  CXoneSdkError,
  HttpResponse,
  UpdateNetworkTimeoutEvent,
} from '@nice-devone/common-sdk';
import { GenericBlockScreenService } from 'src/app/shared/services/gereric-block-screen';

@Injectable({
  providedIn: 'root',
})
export class SdkAcdAgent {
  cxoneAcd = CXoneAcdClient.instance;
  public instance = this.cxoneAcd;

  // Subjects para eventos
  public agentState$ = new Subject<any>();
  public sessionState$ = new Subject<any>();
  public voiceContactUpdate$ = new Subject<any>();
  public agentLegEvent$ = new Subject<any>();
  public contacts$ = new Subject<any>();

  // Configurações
  private timeOutInit = 30000; //30 segundos
  private subscriptions: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    private modalGeneric: GenericModalService,
    private genericBlockScreen: GenericBlockScreenService,
    private localStorage: LocalStorageService,
    private localStorageUtils: LocalStorageUtils,
    private eventHandler: EventHandlerService,
  ) {}

  async initialize(): Promise<void> {
    this.logger.info('SDK-ACD - Inicializando SDK ACD...');
    //promise será rejeitada após 30 segundos se a inicialização não for concluída
    const timeout = new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('SDK-ACD - Timeout na inicialização')),
        this.timeOutInit,
      ),
    );
    try {
      await Promise.race([this.cxoneAcd.initAcdEngagement(), timeout]);

      if (this.cxoneAcd.session.endSession) {
        this.cxoneAcd.session.endSession({
          forceLogoff: false,
          endContacts: true,
          ignorePersonalQueue: true,
        });
      }
      await this.startOrJoinSession();
    } catch (error) {
      this.logger.error('SDK-ACD - Erro ao inicializar SDK ACD: ', error);
      throw error;
    }
  }

  deserializeScreenPopUrl(screenPopUrl: string) {
    try {
      const objeto: any = {};

      const pares = screenPopUrl.split('|');
      pares.forEach((par) => {
        const idx = par.indexOf(':');
        if (idx > -1) {
          const chave = par.substring(0, idx);
          const valor = par.substring(idx + 1);
          if (chave) {
            try {
              objeto[chave.toLowerCase()] = JSON.parse(valor);
            } catch (e) {
              objeto[chave.toLowerCase()] = valor;
            }
          }
        }
      });
      return objeto;
    } catch (error) {
      throw new Error(`PJ - Erro ao tratar o screenPopUrl: ${error}`);
    }
  }

  public endSessionWhenTabClose(
    forceLogoff: boolean,
    endContacts: boolean,
    ignorePersonalQueue: boolean,
  ): void {
    this.cxoneAcd.session.endSession({
      forceLogoff: forceLogoff,
      endContacts: endContacts,
      ignorePersonalQueue: ignorePersonalQueue,
    });
    this.logger.info('SDK-ACD - Sessão encerrada ao fechar/mudar a aba.');
  }

  private async startOrJoinSession(): Promise<void> {
    try {
      if (
        this.cxoneAcd.session &&
        typeof this.cxoneAcd.session.joinSession === 'function'
      ) {
        await this.cxoneAcd.session
          .joinSession()
          .then((response: any) => {
            this.logger.info('SDK-ACD - Iniciada a sessao pelo joinSession.');
            this.initializeEvents();
          })
          .catch(async (error) => {
            await this.startSessionFallback();
          });
      } else {
        await this.startSessionFallback();
      }
    } catch (error) {
      this.logger.error(
        'SDK-ACD - Erro ao iniciar ou entrar na sessão: ',
        error,
      );
      throw error;
    }
  }

  private async startSessionFallback(): Promise<void> {
    try {
      await this.cxoneAcd.session
        .startSession({
          stationId: '',
          stationPhoneNumber: 'WebRTC',
          inactivityForceLogout: false,
        })
        .then((response: any) => {
          this.logger.info(
            'SDK-ACD - Sessão iniciada com sucesso no fallback.',
          );
          this.initializeEvents();
        })
        .catch((error) => {
          this.logger.error('SDK-ACD - Erro ao iniciar sessão: ', error);
          throw error;
        });
    } catch (error) {
      this.logger.error(
        'SDK-ACD - Erro ao iniciar sessão no fallback: ',
        error,
      );
      throw error;
    }
  }

  public async clearAllEventsAndReconnect() {
    const centralId = this.localStorageUtils.safeGetItem('centralId');
    const centralWithReAuthList = [];

    //Incluir aqui a sua central caso deseja ativar o sistema de reconnect.
    centralWithReAuthList.push(
      'pj',
      'itau-shop',
      'civics',
      'civics-v2',
      'pf',
      'rede-pj',
      'npc',
      'ibba',
      'recuperacao'
    );
    if (centralId && centralWithReAuthList.includes(centralId)) {
      this.logger.info('central com reconnect detectada');
      this.clearSubscriptions();
      this.eventHandler.clearProcessed();
      this.localStorage.clearAllLocalStorage();
      await this.reconnectWithBackoff();
    }
  }

  private clearSubscriptions() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
  }

  /**
   * Reconectar o agente à sessão do ACD sem reinicializar o SDK. (iniciar ou entrar em uma sessão existente)
   * @param maxAttempts
   * @param delay
   * @returns
   */
  private async reconnectWithBackoff(
    maxAttempts = 5,
    delay = 1000,
  ): Promise<void> {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        this.logger.info(
          `SDK-ACD - Tentativa de reconexão (${attempt}/${maxAttempts})...`,
        );
        await this.startOrJoinSession();
        this.logger.info('SDK-ACD - Reconexão bem-sucedida.');
        return;
      } catch (error) {
        this.logger.error(
          `SDK-ACD - Erro de reconexao na tentativa ${attempt} falhou: `,
          error,
        );
        if (attempt < maxAttempts) {
          const backoffDelay = Math.min(
            delay * Math.pow(2, attempt - 1),
            10000,
          ); // Máximo de 10 segundos
          await new Promise((resolve) => setTimeout(resolve, backoffDelay));
        } else {
          throw new Error(
            'SDK-ACD - Todas as tentativas de reconexão falharam.',
          );
        }
      }
    }
  }

  private initializeEvents() {
    if (!this.cxoneAcd.session) {
      this.logger.error(
        'SDK-ACD - Erro: Sessão não inicializada. Eventos não podem ser inicializados.',
      );
      return;
    }

    this.clearSubscriptions();

    this.logger.info('SDK-ACD - Inicializando eventos...');
    try {
      const addSub = (obs: any, handler: (data: any) => void) => {
        const sub = obs?.subscribe(handler);
        if (sub) this.subscriptions.push(sub);
      };

      addSub(
        this.cxoneAcd.session.agentStateService.agentStateSubject,
        (agentState) => {
          this.agentState$.next(agentState);
        },
      );

      addSub(
        this.cxoneAcd.contactManager.voiceContactUpdateEvent,
        (contactUpdate) => {
          this.voiceContactUpdate$.next(contactUpdate);
        },
      );

      addSub(
        this.cxoneAcd.session.onAgentSessionChange,
        async (sessionChange) => {
          const { status, data } = sessionChange;
          this.logger.info(
            `SDK-ACD - Session change event: ${status}. Evento recebido: `,
            data,
          );
          let applicationShouldReconnect = false;

          this.handleSessionChangeData(data);

          this.sessionState$.next(sessionChange);

          switch (sessionChange.status) {
            case AgentSessionStatus.JOIN_SESSION_SUCCESS:
            case AgentSessionStatus.SESSION_START: {
              this.logger.info('SDK-ACD - Sessao agente iniciada.');
              break;
            }
            case AgentSessionStatus.SESSION_END: {
              const mensagem = data?.message;
              let logMotivo = '';
              if (mensagem === 'SessionTimeout') {
                logMotivo = 'Sessão encerrada por timeout de inatividade.';
                applicationShouldReconnect = true;
              } else if (mensagem === '') {
                logMotivo = 'Sessão encerrada pelo agente manualmente.';
              } else if (mensagem === null || mensagem === undefined) {
                logMotivo =
                  'Sessão encerrada. Efetue o processo de login novamente.';
              } else if (mensagem === 'RemoteLogoff') {
                logMotivo = 'Sessão encerrada pelo supervisor.';
              } else {
                logMotivo = `Sessão encerrada. Motivo: ${mensagem}`;
              }
              this.logger.warn(
                `SDK-ACD - Sessão encerrada. Motivo: ${logMotivo} | Evento: ${JSON.stringify(data)}`,
              );

              const contactCenterIdAllowedToHaveNewFeatures = [
                'civics',
                'civics-v2',
                'pj',
                'npc',
                'pf',
                'ibba',
                'rede-pj',
                'gerentes-pf',
                'recuperacao'
              ];

              const centralId = this.localStorageUtils.safeGetItem('centralId');

              const centralIdFormateed = centralId?.trim().toLowerCase();
              const shouldUseNewFeatures =
                !!centralIdFormateed &&
                contactCenterIdAllowedToHaveNewFeatures.includes(
                  centralIdFormateed,
                );

              if (shouldUseNewFeatures) {
                if (applicationShouldReconnect) {
                  this.modalGeneric.createModal({
                    title: 'Sessão Encerrada',
                    description: logMotivo,
                    allowCloseModal: true,
                  });
                  this.clearAllEventsAndReconnect();
                } else {
                  const blockMessage = `Para fazer login novamente, atualize a página do navegador (pressione F5 ou clique no botão de recarregar). Se você não quiser se relogar agora, basta fechar esta janela.`;

                  this.genericBlockScreen.createBlockScreen({
                    title: logMotivo,
                    description: blockMessage,
                  });
                }
              } else {
                this.modalGeneric.createModal({
                  title: 'Sessão Encerrada',
                  description: logMotivo,
                  allowCloseModal: true,
                });
                this.clearAllEventsAndReconnect();
              }

              break;
            }
            case AgentSessionStatus.JOIN_SESSION_FAILURE: {
              this.logger.warn(
                `SDK-ACD - Falha ao entrar na sessão. Motivo: ${data?.message} | Evento: ${JSON.stringify(data)}`,
              );
              this.modalGeneric.createModal({
                title: 'Sessão Encerrada',
                description: `Sessão encerrada. Motivo: ${data?.message}`,
                allowCloseModal: true,
              });
              this.clearAllEventsAndReconnect();
              break;
            }
          }
        },
      );

      addSub(
        this.cxoneAcd.session.networkOfflineSubject,
        async (offlineStatus) => {
          if (offlineStatus instanceof UpdateNetworkTimeoutEvent) {
            const totalRequests = offlineStatus.totalNetworkRequestExecuted;
            const retryStatus = offlineStatus.retryStatus;
            this.logger.error(
              `SDK-ACD - Erro de conexão com a rede. Total de requisições executadas: ${totalRequests}, Status de tentativa: ${retryStatus}`,
            );
            if (offlineStatus.parseData) {
              offlineStatus.parseData(offlineStatus);
              this.logger.info(
                'SDK-ACD - Dados adicionais do evento de timeout de rede foram processados.',
                offlineStatus,
              );
            }
            this.modalGeneric.createModal({
              title: 'Problema de Conexão',
              description: `Detectamos um problema de conexão com a rede. Por favor, verifique sua conexão.`,
              allowCloseModal: true,
            });
          } else {
            this.logger.warn(
              'SDK-ACD - Status de rede offline recebido: ',
              offlineStatus,
            );
          }
        },
      );

      addSub(this.cxoneAcd.session.agentLegEvent, (agentLegUpdate) => {
        this.agentLegEvent$.next(agentLegUpdate);
      });

      /*addSub(
        this.cxoneAcd.contactManager.voiceMailContactUpdateEvent,
        (voiceMailUpdate) => {
          this.logger.info(
            'SDK-ACD - Atualização de contato de correio de voz: ',
            voiceMailUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.contactManager.conferenceCallEvent,
        (conferenceUpdate) => {
          this.logger.info(
            'SDK-ACD - Atualização de chamada de conferência: ',
            conferenceUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.contactManager.workItemContactUpdateEvent,
        (workItemUpdate) => {
          this.logger.info(
            'SDK-ACD - Atualização de chamada de conferência: ',
            workItemUpdate,
          );
        },
      );

      addSub(this.cxoneAcd.contactManager.onUserSlotEvent, (userSlotUpdate) => {
        this.logger.info(
          'SDK-ACD - Atualização de slot de usuário: ',
          userSlotUpdate,
        );
      });

      addSub(
        this.cxoneAcd.contactManager.onDispositionEvent,
        (dispositionUpdate) => {
          this.logger.info(
            'SDK-ACD - Atualização de disposição: ',
            dispositionUpdate,
          );
        },
      );

      addSub(this.cxoneAcd.contactManager.onTagsEvent, (tagsUpdate) => {
        this.logger.info('SDK-ACD - Atualização de tags do contato: ', tagsUpdate);
      });

      addSub(
        this.cxoneAcd.contactManager.voiceMailPlayBackEvent,
        (playBackUpdate) => {
          this.logger.info(
            'SDK-ACD - Atualização de reprodução de correio de voz: ',
            playBackUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.contactManager.conferenceCallEvent,
        (conferenceUpdate) => {
          this.logger.info(
            'SDK-ACD - Conference call update received: ',
            conferenceUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.contactManager.onDigitalContactUserTypingPreviewEvent,
        (typingPreviewUpdate) => {
          this.logger.info(
            'SDK-ACD - Usuário digitando em contato digital (preview): ',
            typingPreviewUpdate,
          );
        },
      );

      addSub(this.cxoneAcd.contactManager.onCoBrowseEvent, (coBrowseUpdate) => {
        this.logger.info(
          'SDK-ACD - Atualização de co-navegação: ',
          coBrowseUpdate,
        );
      });

      addSub(
        this.cxoneAcd.contactManager.onDigitalContactNewMessageEvent,
        (newMessageUpdate) => {
          this.logger.info(
            'SDK-ACD - Nova mensagem recebida em contato digital: ',
            newMessageUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.session.naturalCallingSkillListSubject,
        (skillList) => {
          this.logger.info(
            'SDK-ACD - Lista de habilidades de chamadas naturais recebida: ',
            skillList,
          );
        },
      );

      addSub(
        this.cxoneAcd.session.hoursOfOperationSubject,
        (hoursOfOperation) => {
          this.logger.info(
            'SDK-ACD - Horário de funcionamento recebido: ',
            hoursOfOperation,
          );
        },
      );

      addSub(
        this.cxoneAcd.notification.onCXoneNotificationEvent,
        (notificationUpdate) => {
          this.logger.info(
            'SDK-ACD - Notificação recebida do CXone: ',
            notificationUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.notification.onUpdateMessageEvent,
        (updateMessage) => {
          this.logger.info(
            'SDK-ACD - Mensagem de atualização recebida: ',
            updateMessage,
          );
        },
      );

      addSub(
        this.cxoneAcd.indicator.contactIndicatorsEvent,
        (indicatorUpdate) => {
          this.logger.info(
            'SDK-ACD - Indicadores do contato atualizados: ',
            indicatorUpdate,
          );
        },
      );

      addSub(
        this.cxoneAcd.indicator.agentIndicatorsEvent,
        (agentIndicatorUpdate) => {
          this.logger.info(
            'SDK-ACD - Indicadores do agente atualizados: ',
            agentIndicatorUpdate,
          );
        },
      );*/
    } catch (error) {
      this.logger.error('SDK-ACD - Erro ao ouvir eventos: ', error, {
        context: 'initializeEvents',
      });
      throw error;
    }
  }

  public async getAgentDetails(agentId: string) {
    return await this.cxoneAcd.agentDetailService.getAgentInfoByAgentId(
      agentId,
    );
  }

  /**
   * Lida com os diferentes tipos de dados possíveis no evento de mudança de sessão.
   * @param data Dados recebidos no evento.
   */
  private handleSessionChangeData(
    data: AgentSessionEndEvent | HttpResponse | CXoneSdkError,
  ) {
    if (this.isHttpResponse(data)) {
      this.handleHttpResponse(data);
    } else if (this.isCXoneSdkError(data)) {
      this.handleCXoneSdkError(data);
    }
  }

  /**
   * Verifica se o dado é do tipo HttpResponse.
   * @param data Dados a serem verificados.
   * @returns true se o dado for do tipo HttpResponse, caso contrário false.
   */
  private isHttpResponse(data: any): data is HttpResponse {
    return (
      data &&
      typeof data.body !== 'undefined' &&
      typeof data.status === 'number' &&
      typeof data.statusText === 'string'
    );
  }

  /**
   * Verifica se o dado é do tipo CXoneSdkError.
   * @param data Dados a serem verificados.
   * @returns true se os dados forem do tipo CXoneSdkError, caso contrário false.
   */
  private isCXoneSdkError(data: any): data is CXoneSdkError {
    return (
      data &&
      typeof data.message === 'string' &&
      typeof data.name === 'string' &&
      typeof data.errorType === 'string'
    );
  }

  /**
   * Lida com dados do tipo HttpResponse.
   * @param data Dados do tipo HttpResponse.
   */
  private handleHttpResponse(data: HttpResponse): void {
    const { headers, status, statusText } = data;
    this.logger.warn(
      `SDK-ACD - HttpResponse recebido. Status: ${status}, StatusText: ${statusText} | Evento ${JSON.stringify(data)}`,
    );
    if (headers) {
      this.logger.warn('SDK-ACD - HttpResponse - Headers:', headers);
    }
  }

  /**
   * Lida com dados do tipo CXoneSdkError.
   * @param data Dados do tipo CXoneSdkError.
   */
  private handleCXoneSdkError(data: CXoneSdkError): void {
    /**
     * CXoneSdkErrorType:
        CIRCUIT_OPEN: "CIRCUIT_BREAKER_IS_IN_OPEN_STATE" (Devido à limitação e ao limite de falha excedido além do limite, o disjuntor está em estado aberto)
        CXONE_API_ERROR: "CXONE_API_ERROR" (Tipo de erro a ser usado para erro de API de plataforma)
        DATA_VALIDATION_ERROR: "DATA_VALIDATION_ERROR" (Tipo de erro quando falha na validação em relação ao esquema yup)
        INVALID_METHOD_INVOCATION: "INVALID_METHOD_INVOCATION" (Tipo de erro a ser usado em caso de chamadas de métodos ilegais/não autorizados)
        INVALID_METHOD_PARMS: "INVALID_METHOD_PARMS" (Tipo de erro a ser usado para validar os parâmetros do método)
        NO_DATA_FOUND: "NO_DATA_FOUND" (Tipo de erro a ser usado quando os dados estiverem vazios)
        UNHANDLED_EXCEPTION: "UNHANDLED_EXCEPTION" (Tipo de erro a ser usado para exceção ao invocar APIs)
        WEBSOCKET_ERROR: "WEBSOCKET_ERROR" (Tipo de erro a ser usado para erro de websocket)
        WEB_SOCKET_DATA_VALIDATE: "WEB_SOCKET_DATA_VALIDATE" (Tipo de erro quando falha ao analisar a resposta do websocket conforme o modelo de esquema)
     */
    const { errorType, message, name } = data;
    this.logger.warn(
      `SDK-ACD - CXoneSdkError recebido. Tipo de erro: ${errorType}, Mensagem: ${message}, Nome: ${name} | Evento ${JSON.stringify(data)}`,
    );
    if (data.data && this.isHttpResponse(data.data)) {
      this.logger.warn(
        'SDK-ACD - HttpResponse - Detalhes adicionais do erro: ',
        data.data,
      );
    }
  }
}
